import { useConfig } from '../use-config';
import {
  format,
  parseISO,
  startOfYear,
  getWeek,
  getDate,
  getDay,
  addWeeks,
  startOfWeek,
  differenceInYears,
  differenceInMonths,
  differenceInWeeks,
  differenceInCalendarDays,
} from 'date-fns';

export enum RestrictTo {
  Day,
  Week,
  Month,
}

interface DateUtil {
  formatISODate: (dateToFormat: string, formatString?: string) => string | null | undefined;
  formatTime: (timeToFormat: string, formatString?: string) => string | null | undefined;
  getWeekNo: (source: Date, weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6) => number | undefined;
  formatRelativeInWords: (
    source: Date,
    base?: Date,
    restrict?: RestrictTo,
    dateFormatString?: string,
    timeFormatString?: string,
    includeFormattedDate?: boolean
  ) => string;
}

export function useDate(): DateUtil {
  const { dateFormat, timeFormat } = useConfig();

  const formatISODate = (dateToFormat: string, formatString: string = dateFormat) => {
    if (!dateToFormat) return;

    const parsedDate = parseISO(dateToFormat);

    if (!parsedDate) return;

    return format(parsedDate, formatString);
  };

  const formatTime = (valueToFormat: string, formatString: string = timeFormat) => {
    if (!valueToFormat) return;
    const parsedDate = parseISO(valueToFormat);
    if (!parsedDate) return;
    return format(parsedDate, formatString);
  };

  const getWeekNo = (source: Date, weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1): number | undefined => {
    if (!source) return;
    //Get the start date of the week for the passed in date
    const sourceWeekStartDate = startOfWeek(source, { weekStartsOn: weekStartsOn });
    //Get the year start date eg. Fri 01 Jan 2021
    const startOfYearDate = startOfYear(sourceWeekStartDate);
    //Get the day index, Friday is 5 here
    const firstDay = getDay(startOfYearDate);
    const firstWeekDay =
      firstDay > weekStartsOn ? getDate(startOfWeek(addWeeks(startOfYearDate, 1), { weekStartsOn: weekStartsOn })) : 7;
    return getWeek(source, {
      weekStartsOn: weekStartsOn,
      //@ts-ignore firstWeekContainsDate accepts number between 1 to 7 only
      firstWeekContainsDate: firstWeekDay,
    });
  };

  const getDayRelativeWords = (source: Date, base: Date): string => {
    const ageDays = differenceInCalendarDays(base, source);
    const relativeDateInWords = ageDays > 1 ? `${ageDays} days ago` : ageDays === 1 ? 'Yesterday' : 'Today';
    return relativeDateInWords;
  };

  const getWeekRelativeWords = (source: Date, base: Date): string => {
    let relativeDateInWords = '';
    const ageWeeks = differenceInWeeks(base, source);
    if (ageWeeks >= 1) relativeDateInWords = `${ageWeeks} ${ageWeeks > 1 ? 'weeks' : 'week'} ago`;
    else relativeDateInWords = getDayRelativeWords(source, base);
    return relativeDateInWords;
  };

  const getMonthRelativeWords = (source: Date, base: Date): string => {
    let relativeDateInWords = '';
    const ageMonths = differenceInMonths(base, source);
    if (ageMonths >= 1) relativeDateInWords = `${ageMonths} ${ageMonths > 1 ? 'months' : 'month'} ago`;
    else relativeDateInWords = getWeekRelativeWords(source, base);
    return relativeDateInWords;
  };

  const getYearRelativeWords = (source: Date, base: Date): string => {
    let relativeDateInWords = '';
    const ageYears = differenceInYears(base, source);
    if (ageYears >= 1) relativeDateInWords = `${ageYears} ${ageYears > 1 ? 'years' : 'year'} ago`;
    else relativeDateInWords = getMonthRelativeWords(source, base);
    return relativeDateInWords;
  };

  const formatRelativeInWords = (
    source: Date,
    base: Date = new Date(),
    restrict: RestrictTo = RestrictTo.Day,
    dateFormatString: string = dateFormat,
    timeFormatString: string = timeFormat,
    includeFormattedDate: boolean = false
  ): string => {
    const formatString = `${dateFormatString} ${timeFormatString}`;
    let dateInRelativeWords = ``;
    if (restrict == RestrictTo.Day) {
      const ageWeeks = differenceInWeeks(base, source);
      if (ageWeeks >= 1) return format(source, formatString);
      else dateInRelativeWords = getDayRelativeWords(source, base);
    } else if (restrict == RestrictTo.Week) {
      const ageMonths = differenceInMonths(base, source);
      if (ageMonths >= 1) return format(source, formatString);
      else dateInRelativeWords = getWeekRelativeWords(source, base);
    } else if (restrict == RestrictTo.Month) {
      const ageYears = differenceInYears(base, source);
      if (ageYears >= 1) return format(source, formatString);
      else dateInRelativeWords = getMonthRelativeWords(source, base);
    } else {
      dateInRelativeWords = getYearRelativeWords(source, base);
    }
    const formattedDate = includeFormattedDate ? ' (' + format(source, formatString) + ')' : ``;
    return dateInRelativeWords + formattedDate;
  };
  return {
    formatISODate: formatISODate,
    getWeekNo: getWeekNo,
    formatRelativeInWords: formatRelativeInWords,
    formatTime: formatTime,
  };
}
